import React from "react";
import { HOME } from "@/constants/testIds";
import { track } from "@/lib/analytics";

const REGISTER_URL = "https://app.withaurora.love/#/register";
const APPLE_STORE_URL = "https://apps.apple.com/us/app/aurora-ai-relationship-coach/id6765744036";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=love.withaurora.app";

const placementOf = (testId = "") => {
  if (testId.includes("hero")) return "hero";
  if (testId.includes("funnel")) return "funnel";
  if (testId.includes("footer")) return "footer";
  if (testId.includes("faith")) return "faith";
  return "other";
};

export const AppleBadge = ({ className = "", testId = HOME.heroDownloadApple }) => {
  return (
    <a
      href={APPLE_STORE_URL}
      data-testid={testId}
      onClick={() => track("cta_appstore_click", { placement: placementOf(testId) })}
      className={`inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-black/70 px-4 py-2.5 transition-all hover:border-white/40 hover:bg-black/85 hover:-translate-y-0.5 ${className}`}
      aria-label="Download on the App Store"
    >
      <svg width="22" height="26" viewBox="0 0 22 26" fill="white" aria-hidden="true">
        <path d="M17.4 13.7c0-3.1 2.5-4.6 2.6-4.7-1.4-2-3.6-2.3-4.4-2.3-1.9-.2-3.6 1.1-4.6 1.1-.9 0-2.4-1.1-3.9-1-2 0-3.9 1.2-4.9 3-2.1 3.7-.5 9.1 1.5 12.1 1 1.5 2.2 3.1 3.7 3 1.5-.1 2.1-.9 3.9-.9 1.8 0 2.3.9 3.9.9 1.6 0 2.6-1.5 3.6-3 1.1-1.7 1.6-3.4 1.6-3.5-.1 0-3-1.2-3-4.7zM14.3 4.4c.8-1 1.4-2.4 1.2-3.8-1.2.1-2.6.8-3.4 1.8-.7.9-1.4 2.3-1.2 3.7 1.3.1 2.6-.7 3.4-1.7z" />
      </svg>
      <div className="flex flex-col leading-none">
        <span className="text-[0.62rem] font-sans tracking-wider uppercase text-white/70">
          Download on the
        </span>
        <span className="text-[1.05rem] font-sans font-semibold text-white">
          App Store
        </span>
      </div>
    </a>
  );
};

export const GoogleBadge = ({ className = "", testId = HOME.heroDownloadGoogle }) => {
  return (
    <a
      href={GOOGLE_PLAY_URL}
      data-testid={testId}
      onClick={() => track("cta_googleplay_click", { placement: placementOf(testId) })}
      className={`inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-black/70 px-4 py-2.5 transition-all hover:border-white/40 hover:bg-black/85 hover:-translate-y-0.5 ${className}`}
      aria-label="Get it on Google Play"
    >
      <svg width="22" height="24" viewBox="0 0 24 26" aria-hidden="true">
        <defs>
          <linearGradient id="gp1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00C3FF" />
            <stop offset="100%" stopColor="#0078FF" />
          </linearGradient>
          <linearGradient id="gp2" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#FFD600" />
            <stop offset="100%" stopColor="#FFAA00" />
          </linearGradient>
          <linearGradient id="gp3" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF3A44" />
            <stop offset="100%" stopColor="#C71D38" />
          </linearGradient>
          <linearGradient id="gp4" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00DA9C" />
            <stop offset="100%" stopColor="#00A87A" />
          </linearGradient>
        </defs>
        <path d="M2 2 L14 13 L2 24 Z" fill="url(#gp1)" />
        <path d="M14 13 L21 9 L21 17 Z" fill="url(#gp2)" />
        <path d="M2 2 L14 13 L21 9 Z" fill="url(#gp4)" />
        <path d="M2 24 L14 13 L21 17 Z" fill="url(#gp3)" />
      </svg>
      <div className="flex flex-col leading-none">
        <span className="text-[0.62rem] font-sans tracking-wider uppercase text-white/70">
          GET IT ON
        </span>
        <span className="text-[1.05rem] font-sans font-semibold text-white">
          Google Play
        </span>
      </div>
    </a>
  );
};

export const StartFreeButton = ({
  label = "Start Free Now",
  testId = HOME.heroStartFree,
  className = "",
}) => (
  <a
    href={REGISTER_URL}
    data-testid={testId}
    onClick={() => track("cta_register_click", { placement: placementOf(testId) })}
    className={`btn-aurora ${className}`}
  >
    <span>{label}</span>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12h14m0 0l-6-6m6 6l-6 6"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </a>
);

export const StoreBadgeGroup = ({
  showStart = true,
  className = "",
  appleTestId,
  googleTestId,
  startTestId,
  startLabel,
}) => (
  <div className={`flex flex-wrap items-center gap-3 ${className}`}>
    {showStart && (
      <StartFreeButton testId={startTestId} label={startLabel} />
    )}
    <AppleBadge testId={appleTestId} />
    <GoogleBadge testId={googleTestId} />
  </div>
);
